/*
  Execution, wave 3 (screen 09 idea 8): site mode on the phone.
  Task cards with their progress; a card opens into the places it is worked (a panel, a floor); a place opens
  into a checklist of its devices with 44px rows (the whole row is the tap target). "Mark as checked" fills
  Checked by with the signed-in person, "Photo" proves the work (one photo per device, or for the whole panel).
  Ticks and photos go through the phone's outbox (siteQueue.js): made without signal they wait and are sent when
  the connection is back, and the page already shows them as done. "Request VO" raises a variation from site
  (11 #8, SiteVariationSheet).

  What the phone shows comes from the server's answers (an overlay on the loaded tasks): a sent tick shows the
  stored value, a tick the server skipped shows the item as it is now, and an entry is dropped once the page's
  own data has caught up. The whole project is NOT reloaded after each tick (about 1.3 MB on a big project, on
  mobile data): the page refreshes once when site mode is left (onSynced), if anything was sent.
*/
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Camera, Check, ChevronDown, ChevronLeft, ChevronRight, CircleCheck, CloudOff, FilePlus2, Hand, Loader2, MapPin, UserPlus,
} from 'lucide-react';
import api, { openAuthenticatedFile } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/ui/status-pill';
import { ProgressBar } from '@/components/ui/progress-bar';
import { notify } from '@/components/ui/notify';
import { getErrorMessage } from '@/lib/errors';
import { formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { MONO } from './ProjectUi';
import { createSiteQueue } from './siteQueue';
import { deviceInfo, duplicateAddressIds, siteLocations, taskProgress } from './siteModel';
import { SiteVariationSheet } from './SiteVariationSheet';

const LIST_STEP = 12;
const text = (value) => String(value ?? '').trim();
const fmt = (value) => formatNumber(value, { decimals: 0 });

function useSiteQueue(projectId, userId, onResult) {
  const [status, setStatus] = useState({ ticks: 0, photos: 0, error: '', waiting: '', online: true });
  const resultRef = useRef(onResult);
  resultRef.current = onResult;
  const [queue, setQueue] = useState(null);
  // Made in the effect (not useMemo): React StrictMode mounts twice in development, and a memoised queue would
  // stay disposed after the first unmount — no status, no "online" flush, no retry.
  useEffect(() => {
    if (!projectId) return undefined;
    let live = true;
    const next = createSiteQueue({
      projectId,
      userId,
      post: (checks) => api.post(`/projects/${projectId}/execution-tasks/site-checks`, { checks }).then((res) => res.data),
      upload: ({ taskId, file, opId }) => {
        const form = new FormData();
        form.append('file', file, file?.name || 'photo.jpg');
        form.append('op_id', opId);
        return api.post(`/projects/${projectId}/execution-tasks/${taskId}/photos`, form, { headers: { 'Content-Type': 'multipart/form-data' } }).then((res) => res.data);
      },
      onChange: (state) => {
        setStatus({ ticks: state.ticks, photos: state.photos, error: state.error, waiting: state.waiting, online: state.online });
        if (state.result) resultRef.current?.(state.result);
      },
    });
    setQueue(next);
    void next.status().then((state) => { if (live) setStatus(state); });
    void next.flush();
    return () => {
      live = false;
      next.dispose();
      setQueue((current) => (current === next ? null : current));
    };
  }, [projectId, userId]);
  return { queue, status };
}

function SyncState({ status }) {
  const waiting = status.ticks + status.photos;
  if (status.error) {
    return <StatusPill tone="red" label={status.error} size="sm" data-testid="site-sync" />;
  }
  if (waiting) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-md bg-status-amber-bg px-2 py-1 text-[12px] font-semibold text-status-amber-ink" data-testid="site-sync">
        <CloudOff className="h-3.5 w-3.5" aria-hidden="true" />
        {waiting} waiting{status.waiting ? ` · ${status.waiting}` : ' for signal — sends by itself'}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-status-green-ink" data-testid="site-sync">
      <CircleCheck className="h-3.5 w-3.5" aria-hidden="true" />
      All ticks sent
    </span>
  );
}

function DeviceRow({ leaf, mainTitle, selected, checked, checkedBy, duplicate, onToggle, photos, onOpenPhoto }) {
  const info = deviceInfo(leaf, mainTitle);
  return (
    <li className={cn('flex items-center transition-colors', selected ? 'bg-brand-soft' : duplicate ? 'bg-status-red-bg' : 'hover:bg-surface-hover')}>
      <div
        role="checkbox"
        aria-checked={selected}
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(event) => {
          if (event.key === ' ' || event.key === 'Enter') {
            event.preventDefault();
            onToggle();
          }
        }}
        className={cn(
          'flex min-h-[60px] min-w-0 flex-1 cursor-pointer items-center gap-3 py-2 pl-3 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
          photos.length ? 'pr-1' : 'pr-3'
        )}
        data-testid={`site-device-${leaf.id}`}
      >
        <span
          className={cn(
            'grid h-11 w-11 shrink-0 place-items-center rounded-full border-2 transition-colors',
            selected ? 'border-brand bg-brand text-brand-fg' : 'border-line-2 bg-surface text-transparent'
          )}
          aria-hidden="true"
        >
          <Check className="h-5 w-5" strokeWidth={3} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[15px] font-semibold text-ink">{info.name}</span>
          <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12.5px] text-ink-3">
            {info.room ? <span>{info.room}</span> : null}
            {duplicate ? <StatusPill tone="red" label="Duplicate" size="sm" /> : null}
            {checked ? (
              <span className="inline-flex items-center gap-1 font-semibold text-status-green-ink">
                <CircleCheck className="h-3.5 w-3.5" aria-hidden="true" />
                Checked{checkedBy ? ` · ${checkedBy}` : ''}
              </span>
            ) : null}
          </span>
        </span>
        {info.address ? <span className={cn(MONO, 'shrink-0 text-[14px]', duplicate ? 'font-semibold text-status-red-ink' : 'text-ink-2')}>{info.address}</span> : null}
      </div>
      {/* next to the checkbox, not inside it: a button within role="checkbox" is a nested interactive element */}
      {photos.length ? (
        <button
          type="button"
          onClick={() => onOpenPhoto(photos[photos.length - 1])}
          className="mr-2 inline-flex h-11 shrink-0 items-center gap-1 rounded-md px-2 text-[12px] font-semibold text-brand-ink hover:bg-surface-hover"
          aria-label={`${photos.length} photo${photos.length === 1 ? '' : 's'} of ${info.name}`}
          data-testid={`site-device-photos-${leaf.id}`}
        >
          <Camera className="h-4 w-4" aria-hidden="true" />
          {photos.length}
        </button>
      ) : null}
    </li>
  );
}

export function SiteMode({
  projectId, tasks = [], tree, userName = '', canEdit, canRaiseVo, reviewer, onEdit, onSynced,
}) {
  const [openTaskId, setOpenTaskId] = useState(null);
  const [openLocationId, setOpenLocationId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [limit, setLimit] = useState(LIST_STEP);
  const [overlay, setOverlay] = useState(() => new Map()); // taskId → { checked_by, checked_at }
  const [photoOverlay, setPhotoOverlay] = useState(() => new Map()); // taskId → photos
  const [voOpen, setVoOpen] = useState(false);
  const photoInput = useRef(null);
  const auth = useAuth();
  const userId = auth?.user?.id || '';

  // The page reloads the project once, when site mode is left (List view, another tab), not after every tick.
  const syncedRef = useRef(false);
  const onSyncedRef = useRef(onSynced);
  onSyncedRef.current = onSynced;
  useEffect(() => {
    const here = typeof window !== 'undefined' ? window.location.pathname : '';
    return () => {
      if (!syncedRef.current) return;
      // still on this project page (another view or tab): refresh it; the page itself was left: nothing to do
      if (typeof window !== 'undefined' && window.location.pathname !== here) return;
      onSyncedRef.current?.();
    };
  }, []);

  const onResult = useCallback((result) => {
    const skipped = result.skipped || [];
    if (result.tasks?.length || skipped.length) {
      setOverlay((prev) => {
        const next = new Map(prev);
        (result.tasks || []).forEach((task) => next.set(task.id, { checked_by: task.checked_by, checked_at: task.checked_at }));
        skipped.forEach((entry) => {
          if (!entry.task_id) return;
          // stale: someone changed it later, show it as it is now; otherwise not saved: show it as loaded
          if (entry.reason === 'stale' && entry.task) next.set(entry.task_id, { checked_by: entry.task.checked_by || '', checked_at: entry.task.checked_at || '' });
          else next.delete(entry.task_id);
        });
        return next;
      });
    }
    if (result.photos?.length) {
      setPhotoOverlay((prev) => {
        const next = new Map(prev);
        result.photos.forEach((entry) => next.set(entry.taskId, entry.photos));
        return next;
      });
    }
    const stale = skipped.filter((entry) => entry.reason === 'stale').length;
    const lost = skipped.length - stale;
    if (stale) notify.warning(`${stale} tick${stale === 1 ? ' was' : 's were'} not applied — someone changed ${stale === 1 ? 'that item' : 'those items'} after you`);
    if (lost) notify.warning(`${lost} tick${lost === 1 ? '' : 's'} could not be saved — the item changed. Reload and tick again.`);
    if (result.applied?.length || result.photos?.length) syncedRef.current = true;
  }, []);
  const { queue, status } = useSiteQueue(projectId, userId, onResult);

  // ticks still waiting in the outbox show as made
  useEffect(() => {
    if (!queue) return;
    const pending = queue.pendingTicks();
    if (!pending.size) return;
    setOverlay((prev) => {
      const next = new Map(prev);
      pending.forEach((checked, taskId) => next.set(taskId, { checked_by: checked ? userName : '', checked_at: checked ? 'pending' : '' }));
      return next;
    });
  }, [queue, userName]);

  // Once the page's own data shows what the overlay says (after a refresh) the entry is dropped, so a later
  // change made elsewhere is not hidden behind it. Ticks still waiting in the outbox stay.
  useEffect(() => {
    const byId = new Map(tasks.map((task) => [task.id, task]));
    const pending = queue ? queue.pendingTicks() : new Map();
    setOverlay((prev) => {
      let next = prev;
      prev.forEach((value, taskId) => {
        const task = byId.get(taskId);
        if (task && !pending.has(taskId) && text(task.checked_by) === text(value.checked_by)) {
          if (next === prev) next = new Map(prev);
          next.delete(taskId);
        }
      });
      return next;
    });
    setPhotoOverlay((prev) => {
      let next = prev;
      prev.forEach((photos, taskId) => {
        const task = byId.get(taskId);
        const ids = (list) => (list || []).map((photo) => photo.id).join('|');
        if (task && ids(task.photos) === ids(photos)) {
          if (next === prev) next = new Map(prev);
          next.delete(taskId);
        }
      });
      return next;
    });
  }, [tasks, queue]);

  const checkedBy = (leaf) => (overlay.has(leaf.id) ? overlay.get(leaf.id).checked_by : text(leaf.checked_by));
  const photosOf = (leaf) => photoOverlay.get(leaf.id) || leaf.photos || [];

  const mains = useMemo(() => {
    const list = tasks.filter((task) => !task.parent_id);
    const rank = (task) => (['Done', 'Not required'].includes(text(task.status)) ? 1 : 0);
    return [...list].sort((a, b) => rank(a) - rank(b));
  }, [tasks]);
  const openTask = mains.find((task) => task.id === openTaskId) || null;
  const locations = useMemo(() => (openTask ? siteLocations(openTask, tree) : []), [openTask, tree]);
  const location = locations.find((entry) => entry.id === openLocationId) || null;
  const duplicates = useMemo(() => (location ? duplicateAddressIds(location.leaves) : new Set()), [location]);

  useEffect(() => { setSelected(new Set()); setLimit(LIST_STEP); }, [openLocationId, openTaskId]);

  const toggle = (leafId) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(leafId)) next.delete(leafId); else next.add(leafId);
    return next;
  });
  const selectedLeaves = location ? location.leaves.filter((leaf) => selected.has(leaf.id)) : [];
  const allSelectedChecked = selectedLeaves.length > 0 && selectedLeaves.every((leaf) => checkedBy(leaf));

  const markChecked = () => {
    if (!queue || !selectedLeaves.length) return;
    const checked = !allSelectedChecked;
    setOverlay((prev) => {
      const next = new Map(prev);
      selectedLeaves.forEach((leaf) => next.set(leaf.id, { checked_by: checked ? userName : '', checked_at: checked ? new Date().toISOString() : '' }));
      return next;
    });
    selectedLeaves.forEach((leaf) => queue.enqueueCheck({ taskId: leaf.id, checked }));
    notify.success(`${selectedLeaves.length} device${selectedLeaves.length === 1 ? '' : 's'} ${checked ? 'checked' : 'unchecked'}${typeof navigator !== 'undefined' && navigator.onLine === false ? ' — sends when there is signal' : ''}`);
    setSelected(new Set());
    void queue.flush();
  };

  const takePhoto = async (file) => {
    if (!queue || !file || !location) return;
    const target = selectedLeaves.length === 1 ? selectedLeaves[0].id : location.container.id;
    await queue.enqueuePhoto({ taskId: target, file });
    notify.success(selectedLeaves.length === 1 ? 'Photo added to the device' : `Photo added to ${location.label}`);
    void queue.flush();
  };

  const openPhoto = async (photo) => {
    try {
      await openAuthenticatedFile(photo.url || `/api/projects/${projectId}/execution-photos/${photo.file}`);
    } catch (err) {
      notify.error('Could not open the photo', { description: getErrorMessage(err) });
    }
  };

  const locationPhotos = location ? photosOf(location.container) : [];

  return (
    <div className="space-y-3 pb-24" data-testid="site-mode">
      <div className="flex flex-wrap items-center justify-between gap-2 px-1">
        <SyncState status={status} />
        {canRaiseVo ? (
          <Button type="button" variant="outline" size="sm" onClick={() => setVoOpen(true)} data-testid="site-request-vo">
            <FilePlus2 aria-hidden="true" />
            Request VO
          </Button>
        ) : null}
      </div>
      <ul className="space-y-2.5">
        {mains.map((task) => {
          const progress = taskProgress(task, tree);
          const open = task.id === openTaskId;
          const closed = ['Done', 'Not required'].includes(text(task.status));
          const notRequired = text(task.status) === 'Not required';
          const hasItems = progress.total > 0 && !notRequired;
          return (
            <li key={task.id} className="overflow-hidden rounded-card border border-line bg-surface shadow-card" data-testid={`site-task-${task.id}`}>
              <button
                type="button"
                className="flex w-full items-start gap-2 px-4 py-3.5 text-left"
                onClick={() => { setOpenTaskId(open ? null : task.id); setOpenLocationId(null); }}
                aria-expanded={open}
                disabled={!hasItems}
              >
                {hasItems ? (open ? <ChevronDown className="mt-1 h-4 w-4 shrink-0 text-ink-3" aria-hidden="true" /> : <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-ink-3" aria-hidden="true" />) : <span className="w-4 shrink-0" />}
                <span className="min-w-0 flex-1">
                  <span className="flex items-start justify-between gap-2">
                    <span className="text-[16px] font-bold leading-snug text-ink">{task.title}</span>
                    <StatusPill status={task.status || 'Not Started'} domain="execution" size="sm" />
                  </span>
                  {hasItems ? (
                    <span className="mt-2 flex items-center gap-3">
                      <ProgressBar value={(progress.done / progress.total) * 100} label={`${task.title} done`} className="max-w-[160px] flex-1" />
                      <span className="whitespace-nowrap text-[13px] font-semibold tabular-nums text-ink-2">{fmt(progress.done)}/{fmt(progress.total)} <span className="font-normal text-ink-3">{progress.unit}</span></span>
                      <span className="ml-auto rounded-md border border-line px-2 py-0.5 text-[12px] text-ink-2">{task.category || 'Task'}</span>
                    </span>
                  ) : (
                    <span className="mt-1 block text-[13px] text-ink-3">{notRequired ? ['Nothing to check', task.category].filter(Boolean).join(' · ') : [text(task.done_by) || 'Unassigned', task.category].filter(Boolean).join(' · ')}</span>
                  )}
                </span>
              </button>
              {!hasItems && !closed && canEdit && !text(task.done_by) && userName ? (
                <div className="px-4 pb-3.5 pl-10">
                  <Button type="button" variant="outline" className="h-11" onClick={() => onEdit?.(task.id, 'done_by', userName)} data-testid={`site-assign-${task.id}`}>
                    <UserPlus aria-hidden="true" />
                    Assign to me
                  </Button>
                </div>
              ) : null}

              {open && !location ? (
                <ul className="divide-y divide-line-2 border-t border-line-2" data-testid="site-locations">
                  {locations.map((entry) => {
                    const checkedCount = entry.leaves.filter((leaf) => checkedBy(leaf)).length;
                    return (
                      <li key={entry.id}>
                        <button type="button" className="flex min-h-[56px] w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-surface-hover" onClick={() => setOpenLocationId(entry.id)} data-testid={`site-location-${entry.id}`}>
                          <MapPin className="h-4 w-4 shrink-0 text-ink-3" aria-hidden="true" />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-[14.5px] font-semibold text-ink">{entry.label}</span>
                            <span className="text-[12.5px] text-ink-3">Done {fmt(entry.done)}/{fmt(entry.total)} · Checked {fmt(checkedCount)}/{fmt(entry.total)}</span>
                          </span>
                          <ChevronRight className="h-4 w-4 shrink-0 text-ink-3" aria-hidden="true" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : null}

              {open && location ? (
                <div className="border-t border-line-2" data-testid="site-checklist">
                  <div className="space-y-1 bg-surface-2 px-4 py-3">
                    <button type="button" className="-ml-1 inline-flex items-center gap-1 text-[12.5px] font-semibold text-brand-ink" onClick={() => setOpenLocationId(null)}>
                      <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                      All places
                    </button>
                    <p className="flex items-center gap-1.5 text-[15px] font-bold text-ink">
                      <MapPin className="h-4 w-4 shrink-0 text-ink-3" aria-hidden="true" />
                      <span className="min-w-0 truncate">{location.label}</span>
                    </p>
                    <p className="text-[13px] text-ink-3">
                      Done {fmt(location.done)}/{fmt(location.total)}{text(location.container.done_by) ? ` by ${text(location.container.done_by).split(',')[0]}` : ''}
                      {' · '}
                      <b className="font-semibold text-status-amber-ink">Checked {fmt(location.leaves.filter((leaf) => checkedBy(leaf)).length)}/{fmt(location.total)}</b>
                      {selected.size ? <> · <b className="font-semibold text-ink">{selected.size} selected</b></> : null}
                      {locationPhotos.length ? (
                        <button type="button" className="ml-2 inline-flex items-center gap-1 font-semibold text-brand-ink" onClick={() => openPhoto(locationPhotos[locationPhotos.length - 1])}>
                          <Camera className="h-3.5 w-3.5" aria-hidden="true" />
                          {locationPhotos.length}
                        </button>
                      ) : null}
                    </p>
                  </div>
                  <p className="flex items-center gap-1.5 border-b border-line-2 px-4 py-2 text-[12.5px] text-ink-3">
                    <Hand className="h-3.5 w-3.5" aria-hidden="true" />
                    Tap = select · the whole row is the tap target · then Mark as checked
                  </p>
                  <ul className="divide-y divide-line-2">
                    {[...location.leaves]
                      .sort((a, b) => (duplicates.has(b.id) ? 1 : 0) - (duplicates.has(a.id) ? 1 : 0))
                      .slice(0, limit)
                      .map((leaf) => (
                        <DeviceRow
                          key={leaf.id}
                          leaf={leaf}
                          mainTitle={task.title}
                          selected={selected.has(leaf.id)}
                          checked={Boolean(checkedBy(leaf))}
                          checkedBy={checkedBy(leaf)}
                          duplicate={duplicates.has(leaf.id)}
                          photos={photosOf(leaf)}
                          onOpenPhoto={openPhoto}
                          onToggle={() => (canEdit ? toggle(leaf.id) : null)}
                        />
                      ))}
                  </ul>
                  {location.leaves.length > limit ? (
                    <button type="button" className="flex min-h-[48px] w-full items-center gap-2 border-t border-line-2 px-4 text-[14px] font-semibold text-brand-ink" onClick={() => setLimit((value) => value + LIST_STEP * 4)}>
                      <ChevronDown className="h-4 w-4" aria-hidden="true" />
                      {location.leaves.length - limit} more on this {location.leaves.some((leaf) => leaf.device_type) ? 'panel' : 'list'}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>

      {location && canEdit ? (
        <div className="fixed inset-x-0 bottom-[var(--app-bottom-nav-height,0px)] z-30 flex items-center gap-2 border-t border-line bg-surface/95 px-4 py-3 shadow-raised backdrop-blur" data-testid="site-actions">
          <Button type="button" variant="outline" className="h-[52px] w-[64px] flex-col gap-0.5 px-0 text-[11.5px]" onClick={() => photoInput.current?.click()} data-testid="site-photo">
            <Camera className="h-5 w-5" aria-hidden="true" />
            Photo
          </Button>
          <input
            ref={photoInput}
            type="file"
            accept="image/*"
            capture="environment"
            className="sr-only"
            aria-label="Take a photo"
            tabIndex={-1}
            onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ''; void takePhoto(file); }}
          />
          <Button type="button" className="h-[52px] flex-1 text-[15.5px]" disabled={!selectedLeaves.length} onClick={markChecked} data-testid="site-mark-checked">
            {status.ticks ? <Loader2 className="animate-spin" aria-hidden="true" /> : <CircleCheck aria-hidden="true" />}
            {allSelectedChecked ? 'Mark as not checked' : 'Mark as checked'}
            {selectedLeaves.length ? <span className="ml-1 grid h-6 min-w-6 place-items-center rounded-full bg-white/20 px-1.5 text-[13px] font-bold">{selectedLeaves.length}</span> : null}
          </Button>
        </div>
      ) : null}

      <SiteVariationSheet
        open={voOpen}
        onOpenChange={setVoOpen}
        projectId={projectId}
        location={location ? location.label : ''}
        reviewer={reviewer}
      />
    </div>
  );
}
